<template>
  <MeetingProposalHeader
    :number-of-replies="numberOfReplies"
    @add-todo="openTodoModal"
  />
  <MeetingProposalContent :meeting="props.meeting" @add-todo="openTodoModal" />
</template>
<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, inject } from 'vue'
import { storeToRefs } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import * as MeetingsApi from '@app/application/client_meetings/api/meetings'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'
import useI18n from '@app/shared/vue/composables/use-I18n'

import type {
  Meeting,
  PastPageData,
} from '@app/application/client_meetings/types'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import type { Remark } from '@app/application/communication/types'
import MeetingProposalHeader from './meeting-proposal-header.vue'
import MeetingProposalContent from './meeting-proposal-content.vue'
import type { TodoModalData } from '@app/application/client_meetings/types'

const pageData = inject('pageData') as PastPageData

const I18n = useI18n()

interface Props {
  meeting: Meeting
}

const props = defineProps<Props>()

const proposalStore = useProposalDetailsStore()
const pastMeetingsStore = usePastMeetingsStore()
const { repliesByProposalIdTalkingPointId } = storeToRefs(proposalStore)

const { talkingPointTagIds: selectedTagIds } = useTalkingPointTag()

const numberOfReplies = computed(() => {
  const replies =
    repliesByProposalIdTalkingPointId.value?.[props.meeting.proposalId!] || {}
  return Object.keys(replies).length
})

function openTodoModal(data?: TodoModalData): void {
  const { text = '', todoIndex = null } = data || {}

  window.modalCitizen.openModal('new-remark', {
    type: 'todo',
    ownerType: 'ClientMeeting',
    ownerId: props.meeting.id,
    clientMeetingId: props.meeting.id,
    hideTypeSelector: true,
    allRemarkTags: window.allRemarkTags,
    showTags: true,
    ledgerLabel: pageData.ledgerLabel,
    isClientMeetingRemark: true,
    selectedTagIds: selectedTagIds.value,
    text,
    callbackPayload: {
      meetingId: props.meeting.id,
      ...(todoIndex !== null && {
        proposalId: props.meeting.proposalId!,
        todoIndex,
      }),
    },
    title: I18n.t('client_meetings.past.todos.add_todo_title'),
    saveButtonLabel: I18n.t(
      'client_meetings.past.todos.add_todo_save_button_label'
    ),
  })
}

async function refreshCommunicationData(data?: {
  remark: Remark
  action?: string
  callbackPayload?: {
    meetingId?: number
    proposalId?: number
    todoIndex?: number
  }
}): Promise<void> {
  const isCreateRemarkForCurrentMeeting =
    data?.action === 'createRemark' &&
    data?.callbackPayload?.meetingId === props.meeting.id

  if (!isCreateRemarkForCurrentMeeting) {
    return
  }

  const isProposalTodo =
    data?.callbackPayload?.proposalId === props.meeting.proposalId! &&
    data?.callbackPayload?.todoIndex !== undefined

  if (isProposalTodo) {
    await proposalStore.deleteFollowUpAction(
      props.meeting.proposalId!,
      data?.callbackPayload?.todoIndex as number
    )
  }

  const remarksOrder = [...props.meeting.order, `Remark|${data.remark.id}`]
  try {
    await MeetingsApi.update({ id: props.meeting.id, order: remarksOrder })
  } catch (e) {
    SfLog.warn('Error while setting meeting remarks order', {
      extra: { error: e, meetingId: props.meeting.id },
    })
  }
  pastMeetingsStore.setMeetingOrder(props.meeting.id, remarksOrder)
}

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>
